import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AccountCheck.css';
import { logout, checkAuth, handleAuthError } from '../utils/auth';
import { API_URL } from '../config/data';

const AccountCheck = () => {
  // Состояния для данных
  const [user, setUser] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [tradingAccounts, setTradingAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Состояния для формы
  const [formData, setFormData] = useState({
    tradingAccountId: '',
    bankAccountId: ''
  });
  
  // Состояние для найденного счета
  const [selectedTradingAccount, setSelectedTradingAccount] = useState(null);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  
  // Состояние для модального окна успеха
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Функция для получения токена из localStorage
  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  // Функция для получения профиля пользователя
  const fetchUserProfile = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.user;
    } catch (error) {
      console.error('Ошибка при получении профиля:', error);
      throw error;
    }
  };

  // Функция для получения банковских счетов пользователя
  const fetchBankAccounts = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.accounts;
    } catch (error) {
      console.error('Ошибка при получении банковских счетов:', error);
      throw error;
    }
  };

  // Функция для получения торговых счетов пользователя
  const fetchTradingAccounts = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/trading-accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.accounts;
    } catch (error) {
      console.error('Ошибка при получении торговых счетов:', error);
      throw error;
    }
  };

  // Функция для поиска торгового счета по ID
  const findTradingAccountById = (accountId) => {
    if (!accountId || !tradingAccounts.length) {
      setSelectedTradingAccount(null);
      return;
    }

    const foundAccount = tradingAccounts.find(account => 
      account.id.toString() === accountId
    );
    
    setSelectedTradingAccount(foundAccount || null);
  };

  // Функция для поиска банковского счета по ID
  const findBankAccountById = (accountId) => {
    if (!accountId || !bankAccounts.length) {
      setSelectedBankAccount(null);
      return;
    }

    const foundAccount = bankAccounts.find(account => 
      account.id.toString() === accountId
    );
    
    setSelectedBankAccount(foundAccount || null);
  };

  // Функция для обновления данных формы
  const handleFormDataChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Если изменяется ID торгового счета, ищем соответствующий счет
    if (field === 'tradingAccountId') {
      findTradingAccountById(value);
    }
    
    // Если изменяется ID банковского счета, ищем соответствующий счет
    if (field === 'bankAccountId') {
      findBankAccountById(value);
    }
  };

  // Функция для получения имени пользователя
  const getUserDisplayName = () => {
    if (!user) return 'Загрузка...';
    return `${user.lastName} ${user.firstName}`;
  };

  // Функция для получения должности
  const getUserPosition = () => {
    if (!user) return 'Загрузка...';
    return user.role || 'Квалифицированный управляющий';
  };

  // Функция для форматирования суммы
  const formatBalance = (amount) => {
    return new Intl.NumberFormat('ru-RU').format(amount);
  };

  // Обработчики для модального окна
  const handleSuccessPopupClose = (e) => {
    if (e.target === e.currentTarget) {
      setShowSuccessPopup(false);
    }
  };

  const handleSuccessButtonClick = () => {
    setShowSuccessPopup(false);
  };

  // Функция для создания депозита
  const createDeposit = async (e) => {
    e.preventDefault();
    
    // Проверяем, что выбран торговый счет
    if (!selectedTradingAccount) {
      alert('Пожалуйста, выберите торговый счет для пополнения');
      return;
    }

    // Проверяем, что выбран банковский счет
    if (!selectedBankAccount) {
      alert('Пожалуйста, выберите банковский счет для списания');
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      // Подготавливаем данные для отправки
      const depositData = {
        amount: 0, // Автоматически 0, пользователь укажет сумму в банке
        currency: selectedTradingAccount.currency,
        comment: 'Пополнение торгового счета',
        tradingAccountId: selectedTradingAccount.id, // Передаем ID торгового счета
        recipientDetails: {
          accountNumber: selectedTradingAccount.account_number,
          currentBalance: selectedTradingAccount.balance,
          bankAccountNumber: selectedBankAccount.account_number
        }
      };

      const response = await axios.post(`${API_URL}/api/operations/deposit`, depositData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.message === 'Deposit request created successfully') {
        // Сбрасываем форму
        setFormData({
          tradingAccountId: '',
          bankAccountId: ''
        });
        setSelectedTradingAccount(null);
        setSelectedBankAccount(null);
        
        // Показываем модальное окно успеха
        setShowSuccessPopup(true);
      }
    } catch (error) {
      console.error('Ошибка при создании депозита:', error);
      
      // Обрабатываем ошибки авторизации
      if (handleAuthError(error)) {
        return;
      }
      
      alert('Ошибка при создании запроса на пополнение. Попробуйте еще раз.');
    }
  };

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    // Проверяем авторизацию перед загрузкой данных
    if (!checkAuth()) {
      return;
    }

    const loadData = async () => {
      try {
        setIsLoading(true);
        const [userData, bankAccountsData, tradingAccountsData] = await Promise.all([
          fetchUserProfile(),
          fetchBankAccounts(),
          fetchTradingAccounts()
        ]);
        
        setUser(userData);
        setBankAccounts(bankAccountsData);
        setTradingAccounts(tradingAccountsData);
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
        
        // Обрабатываем ошибки авторизации
        if (handleAuthError(error)) {
          return;
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Обработчик для закрытия модального окна по Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showSuccessPopup) {
        setShowSuccessPopup(false);
      }
    };

    if (showSuccessPopup) {
      document.addEventListener('keydown', handleEscape);
      document.body.classList.add('popup-open');
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.classList.remove('popup-open');
      };
    }
  }, [showSuccessPopup]);

  // Показываем загрузку пока данные не загружены
  if (isLoading) {
    return (
      <div className="wrapper">
        <div className="loading">
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper">
      <header data-fls-header-accaunt="" className="header-accaunt">
        <div className="header-accaunt__container">
          <div className="header-accaunt__body">
            <a href="/account" className="header-accaunt__logo">
              <img src="/assets/img/logo.svg" alt="Logo" />
            </a>
            <div className="header-accaunt__actions">
              <div className="header-accaunt__settings">
                <a href="/account/setting" className="--icon-settings"></a>
              </div>
              <div className="header-accaunt__profile">
                <a href="" className="--icon-profile">
                  <div className="header-accaunt__content-profile">
                    <span>{getUserDisplayName()}</span>
                    <span>{getUserPosition()}</span>
                  </div>
                </a>
              </div>
              <button 
                data-fls-button="" 
                type="button" 
                className="header-accaunt__logout header-accaunt__logout--icon-log-out"
                onClick={logout}
              ></button>
            </div>
          </div>
        </div>
      </header>
      <main className="page account">
        <div className="account__container">
          <div className="account__body">
            <nav data-fls-block="" data-fls-aside="" className="aside">
              <ul className="aside__list">
                <li className="aside__item">
                  <a href="/account" className="aside__link aside__link--icon-home">Главная</a>
                </li>
                <li className="aside__item">
                  <a href="/account/expense" className="aside__link aside__link--active aside__link--icon-wallet">Счета</a>
                </li>
                <li className="aside__item">
                  <a href="/account/history" className="aside__link aside__link--icon-history">История</a>
                </li>
                <li className="aside__item">
                  <a href="#" className="aside__link aside__link--icon-withdraw">Вывести</a>
                </li>
              </ul>
              <div className="aside__actions">
                <a href="/account/withdraw" className="aside__action aside__action--icon-withdraw">Вывести деньги</a>
                <a href="/account/check" className="aside__action aside__action--icon-deposit">Пополнить баланс</a>
              </div>
            </nav>
            <div className="account__content">
              <h3 className="account__title account__title--title-25">Пополнение по счету</h3>
              <form onSubmit={createDeposit} data-fls-form="" data-fls-maxwidth="" className="account__check check">
                <div data-fls-block="" className="check__block">
                  <h4 className="check__subtitle check__subtitle--title-20">Торговый счет для пополнения</h4>
                  <div className="check__inputs">
                    <select 
                      data-fls-input="" 
                      required 
                      name="tradingAccountId" 
                      value={formData.tradingAccountId}
                      onChange={(e) => handleFormDataChange('tradingAccountId', e.target.value)}
                      data-error="Error" 
                      className="input" 
                    >
                      <option value="">Выберите торговый счет</option>
                      {tradingAccounts.map(account => {
                        const totalBalance = parseFloat(account.balance) || 0;
                        return (
                          <option key={account.id} value={account.id}>
                            {account.account_number} ({account.currency}) - {formatBalance(totalBalance)} {account.currency}
                          </option>
                        );
                      })}
                    </select>
                    <div className="check__plsdata plsdata">
                      <input 
                        data-fls-input="" 
                        autoComplete="off" 
                        type="text" 
                        name="plsdata" 
                        data-error="Error" 
                        className="plsdata__input input" 
                        disabled
                        value=""
                      />
                      <div className="plsdata__content">
                        Текущий баланс: {selectedTradingAccount ? `${formatBalance(parseFloat(selectedTradingAccount.balance) || 0)} ${selectedTradingAccount.currency}` : 'Счет не выбран'}
                      </div>
                    </div>
                  </div>
                </div>
                <div data-fls-block="" className="check__block">
                  <h4 className="check__subtitle check__subtitle--title-20">Банковский счет для списания</h4>
                  <div className="check__text">
                    <p>Выберите банковский счет, с которого планируете отправить средства</p>
                  </div>
                  <div className="check__inputs">
                    <select 
                      data-fls-input="" 
                      required 
                      name="bankAccountId" 
                      value={formData.bankAccountId}
                      onChange={(e) => handleFormDataChange('bankAccountId', e.target.value)}
                      data-error="Error" 
                      className="input" 
                    >
                      <option value="">Выберите банковский счет</option>
                      {bankAccounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.number} {account.bank} ({account.currency})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button type="submit" data-fls-button="" className="check__button button">Сформировать счет</button>
              </form>
            </div>
          </div>
        </div>
      </main>

      {/* Модальное окно успеха */}
      {showSuccessPopup && (
        <div 
          data-fls-popup="success" 
          aria-hidden="false" 
          className={`popup ${showSuccessPopup ? 'show' : ''}`} 
          onClick={handleSuccessPopupClose}
        >
          <div data-fls-popup-wrapper="" className="popup__wrapper">
            <div data-fls-popup-body="" className="popup__body success">
              <h3 className="success__title success__title--title-20">Успешно!</h3>
              <div className="success__content">
                <p className="success__message">
                  Заявка сформирована, в ближайшее время с вами свяжется администратор для выполнения платежа.
                </p>
              </div>
              <div className="success__actions">
                <button 
                  data-fls-button="" 
                  type="button" 
                  className="success__button button"
                  onClick={handleSuccessButtonClick}
                >
                  Понятно
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountCheck;